/**
 * Taste 16.1.0 â€” Cart drawer controller
 *
 * Replaces the CartDrawer and CartDrawerItems portion of assets/cart.js.
 * This file expects the theme's CartItems base class and global helpers
 * (trapFocus, removeTrapFocus, PUB_SUB_EVENTS, subscribe) to be loaded by
 * the theme, as in the original Taste 16.1.0 implementation.
 */

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.handleKeyUp = (event) => {
      if (event.code === 'Escape' || event.code === 'ESCAPE') this.close();
    };

    this.handleOverlayClick = (event) => {
      if (event.target.closest('#CartDrawer-Overlay')) this.close();
    };

    this.addEventListener('keyup', this.handleKeyUp);
    // Delegate from the custom element so the handler survives replacement
    // of #CartDrawer's inner HTML during Shopify section rendering.
    this.addEventListener('click', this.handleOverlayClick);

    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    this.syncEmptyState();
  }

  disconnectedCallback() {
    this.removeEventListener('keyup', this.handleKeyUp);
    this.removeEventListener('click', this.handleOverlayClick);
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || cartLink.dataset.cartDrawerBound === 'true') return;

    cartLink.dataset.cartDrawerBound = 'true';
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code === 'Space' || event.code === ' ') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  syncEmptyState() {
    const isEmpty = !this.querySelector('#CartDrawer-CartItems .cart-item');

    // Taste's Liquid markup puts the initial is-empty class on <cart-drawer>
    // and <cart-drawer-items>. Keep both in sync after AJAX section updates.
    this.classList.toggle('is-empty', isEmpty);

    const drawerItems = this.querySelector('cart-drawer-items');
    if (drawerItems) drawerItems.classList.toggle('is-empty', isEmpty);

    const inner = this.querySelector('.drawer__inner');
    if (inner) inner.classList.toggle('is-empty', isEmpty);

    return isEmpty;
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;

    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) {
      this.setSummaryAccessibility(cartDrawerNote);
    }

    this.syncEmptyState();

    window.setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const focusContainer = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty') || this.querySelector('.drawer__inner')
          : this.querySelector('.drawer__inner') || this;

        const focusTarget =
          this.querySelector('.drawer__inner') ||
          this.querySelector('.drawer__close') ||
          focusContainer;

        if (focusContainer && typeof trapFocus === 'function') {
          trapFocus(focusContainer, focusTarget);
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close() {
    this.classList.remove('active');
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(summaryElement) {
    summaryElement.setAttribute('role', 'button');
    summaryElement.setAttribute(
      'aria-expanded',
      summaryElement.parentNode.hasAttribute('open')
    );

    if (summaryElement.nextElementSibling?.id) {
      summaryElement.setAttribute('aria-controls', summaryElement.nextElementSibling.id);
    }

    summaryElement.addEventListener('click', (event) => {
      const summary = event.currentTarget;
      summary.setAttribute(
        'aria-expanded',
        !summary.closest('details')?.hasAttribute('open')
      );
    });
  }

  renderContents(parsedState) {
    if (!parsedState || !parsedState.sections) {
      console.error('[Cart drawer] Shopify returned no section HTML.');
      return;
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const html = parsedState.sections[section.id];
      if (!html) return;

      const currentElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!currentElement) return;

      const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
      const incomingElement = section.selector
        ? parsedDocument.querySelector(section.selector)
        : parsedDocument.getElementById(section.id);

      if (!incomingElement) {
        console.warn(`[Cart drawer] Could not find section target: ${section.id}`);
        return;
      }

      // Replace only the contents of the stable target. The delegated overlay
      // click listener remains attached to <cart-drawer>.
      currentElement.innerHTML = incomingElement.innerHTML;
    });

    this.syncEmptyState();
    this.setHeaderCartIconAccessibility();

    window.setTimeout(() => {
      this.syncEmptyState();
      this.open();
    });
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (!html) return '';

    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    const element = parsedDocument.querySelector(selector);

    return element ? element.innerHTML : '';
  }

  getSectionDOM(html, selector = '.shopify-section') {
    if (!html) return null;

    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');
    return parsedDocument.querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}